import { useQuery } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";

export function useMyMemberProfileQuery(enabled: boolean) {
  return useQuery({
    ...orpc.org.getMyMemberProfile.queryOptions({ input: {} }),
    enabled,
  });
}

export function useFullOrganizationQuery() {
  return useQuery(orpc.org.getFullOrganization.queryOptions({ input: {} }));
}

export function useActiveMemberQuery(enabled: boolean = true) {
  return useQuery({
    ...orpc.org.getActiveMember.queryOptions({ input: {} }),
    enabled,
  });
}

export function useMembersQuery() {
  return useQuery(orpc.org.listMembers.queryOptions({ input: {} }));
}

export function useInvitationsQuery(enabled: boolean) {
  return useQuery({
    ...orpc.admin.listInvitations.queryOptions({ input: {} }),
    enabled,
  });
}

export function useAdminListUsersQuery() {
  return useQuery(orpc.admin.listUsers.queryOptions({ input: {} }));
}

export function usePatientAccountsQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.listUserAccounts.queryOptions({ input: { patientMembershipId } }));
}

export function usePatientRecommendedSourcesQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.listUserRecommendedSources.queryOptions({ input: { patientMembershipId } }));
}

export function usePatientMagicLinkQuery(patientMembershipId: string) {
  return useQuery(orpc.admin.getLatestMagicLink.queryOptions({ input: { patientMembershipId } }));
}

export function useApiKeysQuery() {
  return useQuery(orpc.admin.listApiKeys.queryOptions({ input: {} }));
}

export function useEhrClientIdsQuery() {
  return useQuery(orpc.admin.listEhrClientIds.queryOptions({ input: {} }));
}

/** Gated on the caller's plan-features check — free-tier orgs would 403 and
 *  the default retry policy would fire the request 4 times over ~7s. */
export function useClinicalWorkflowsQuery(enabled = true) {
  return useQuery({
    ...orpc.admin.listClinicalWorkflows.queryOptions({ input: {} }),
    enabled,
    retry: false,
  });
}

export function useBackendQuery(providerType: string) {
  return useQuery(orpc.admin.getBackend.queryOptions({ input: { providerType } }));
}

/** Gated on the caller's plan-features check — see comment on useClinicalWorkflowsQuery. */
export function useBackendConfigsQuery(enabled = true) {
  return useQuery({
    ...orpc.admin.listBackend.queryOptions({ input: {} }),
    enabled,
    retry: false,
  });
}

export function useWebhooksQuery() {
  return useQuery(orpc.webhooks.listWebhooks.queryOptions({ input: {} }));
}

export function useWebhookEventsQuery(endpointId: string | null) {
  return useQuery({
    ...orpc.webhooks.listEvents.queryOptions({ input: { id: endpointId ?? "" } }),
    enabled: !!endpointId,
  });
}

export function useConnectedAppsQuery() {
  return useQuery(orpc.connectedApps.list.queryOptions({ input: {} }));
}
